// ---------------------------------------------------------------------------
// Search-param schemas (UI Layering Sweep Phase 2). URL is UI state (UI-LAW
// §4c): drawer subjects, active tabs, and list filters ride search params,
// validated by hand-written type guards in the repo's root-route style —
// invalid values are dropped, never errored. Screen containers import these
// exported types from here (never from router.tsx — that import cycles); the
// root-owned `agent` param stays on the root route and is merged in by the
// router, so no child schema re-declares it.

import type { RangePreset } from "./dashboardMetrics";
import { isMonthKey } from "./usageActivity";
import {
  canonicalWorkingScopeSearch,
  type WorkingScopeSearch,
} from "./workingScope";

// A non-empty string param (ids/uuids), else absent.
function searchText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// A canonical 8-4-4-4-12 hex UUID, else absent. Phase-3 subject params are
// uuids-only (agent.md ledger convention: subject params carry uuids; openers
// ride `?new=`), so a non-uuid value is invalid and stripped.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function searchUuid(value: unknown): string | undefined {
  return typeof value === "string" && UUID_PATTERN.test(value)
    ? value
    : undefined;
}

// A parseable ISO timestamp kept verbatim (never reformatted), else absent.
function searchIsoTimestamp(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  return Number.isNaN(Date.parse(value)) ? undefined : value;
}

// The dashboard/alignment time-range preset. Invalid values are dropped —
// absent means the screen's default (dashboard 7d, alignment 30d), which is
// also how the default stays omitted from the URL.
function searchRangePreset(value: unknown): RangePreset | undefined {
  return value === "24h" || value === "7d" || value === "30d" || value === "90d"
    ? value
    : undefined;
}

// A comma-list param (e.g. `?status=ready,failed`): tokens outside `allowed`
// are dropped, duplicates collapse, order is preserved. The canonical value
// stays a comma-joined string so the URL keeps its human-readable comma form
// (the router would JSON-serialize an array). Empty after filtering = absent.
function searchCommaList(
  value: unknown,
  allowed: readonly string[],
): string | undefined {
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  const allowedSet = new Set<string>(allowed);
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const raw of value.split(",")) {
    const token = raw.trim();
    if (allowedSet.has(token) && !seen.has(token)) {
      seen.add(token);
      tokens.push(token);
    }
  }
  return tokens.length > 0 ? tokens.join(",") : undefined;
}

// A `FilterBar` facet param (UI-LAW §4b.4): one param per facet, named after
// the facet id, multi-value comma-joined (`?voice=elevenlabs,cartesia`). Unlike
// `searchCommaList` there is no allowed set — facet values are data (provider
// tokens, agent-derived labels), not a closed enum, so an unknown value is kept
// and simply matches nothing. Blank tokens are dropped and duplicates collapse.
function searchFacetTokens(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const raw of value.split(",")) {
    const token = raw.trim();
    if (token.length > 0 && !seen.has(token)) {
      seen.add(token);
      tokens.push(token);
    }
  }
  return tokens.length > 0 ? tokens.join(",") : undefined;
}

/**
 * The generic facet half of any screen's search schema. Later filter-migration
 * batches only append their facet-id tuple; the parsing stays here.
 */
export function parseFacetSearch<TFacetId extends string>(
  search: Record<string, unknown>,
  facetIds: readonly TFacetId[],
): { [Key in TFacetId]?: string } {
  const next: { [Key in TFacetId]?: string } = {};
  for (const id of facetIds) {
    const tokens = searchFacetTokens(search[id]);
    if (tokens !== undefined) {
      next[id] = tokens;
    }
  }
  return next;
}

// The `?new=1` open-drawer flag. The router's search parser may surface it as
// the number 1 or the string "1"; both normalize to the literal 1.
function searchFlag(value: unknown): 1 | undefined {
  return value === 1 || value === "1" ? 1 : undefined;
}

export interface ApiKeysSearch {
  agent_id?: string;
  new?: 1;
}

// `?key=` is reserved by api-keys-settings.md but NOT parsed: no key-detail
// surface exists; do not implement until one ships.
export function parseApiKeysSearch(
  search: Record<string, unknown>,
): ApiKeysSearch {
  const next: ApiKeysSearch = {};
  const agentId = searchText(search.agent_id);
  if (agentId !== undefined) {
    next.agent_id = agentId;
  }
  if (searchFlag(search.new) !== undefined) {
    next.new = 1;
  }
  return next;
}

// /checkout/return post-Stripe landing: Stripe appends `?session_id=<id>` (an
// opaque `cs_...` Checkout Session id, not a uuid). Kept verbatim when present,
// else absent — the screen polls entitlement regardless.
export interface CheckoutReturnSearch {
  session_id?: string;
}

export function parseCheckoutReturnSearch(
  search: Record<string, unknown>,
): CheckoutReturnSearch {
  const sessionId = searchText(search.session_id);
  return sessionId !== undefined ? { session_id: sessionId } : {};
}

// /settings/audit-log applied filters (UI-LAW §4c: active filters are
// URL-addressable). The id filters are free-text (the screen's TextInputs
// accept any id string, so they are NOT uuid-validated); the occurred bounds
// are ISO timestamps kept verbatim. The filter-bar draft stays component
// state; only Apply/Clear write here.
export interface AuditLogSearch {
  actor_id?: string;
  occurred_after?: string;
  occurred_before?: string;
  resource_id?: string;
  resource_type?: string;
}

export function parseAuditLogSearch(
  search: Record<string, unknown>,
): AuditLogSearch {
  const next: AuditLogSearch = {};
  const actorId = searchText(search.actor_id);
  if (actorId !== undefined) {
    next.actor_id = actorId;
  }
  const occurredAfter = searchIsoTimestamp(search.occurred_after);
  if (occurredAfter !== undefined) {
    next.occurred_after = occurredAfter;
  }
  const occurredBefore = searchIsoTimestamp(search.occurred_before);
  if (occurredBefore !== undefined) {
    next.occurred_before = occurredBefore;
  }
  const resourceId = searchText(search.resource_id);
  if (resourceId !== undefined) {
    next.resource_id = resourceId;
  }
  const resourceType = searchText(search.resource_type);
  if (resourceType !== undefined) {
    next.resource_type = resourceType;
  }
  return next;
}

// Shell v2: the former screen-local `?team=` filter migrated to the
// root-owned `?team=` working-team param (docs/09 §4 — members is a
// team-bound settings screen, so the root param is semantically identical).
// The root route owns and validates it; no screen-local `team` key remains.
export interface MembershipsSearch {
  new?: 1;
}

export function parseMembershipsSearch(
  search: Record<string, unknown>,
): MembershipsSearch {
  return searchFlag(search.new) !== undefined ? { new: 1 } : {};
}

export interface InvitationsSearch {
  new?: 1;
}

export function parseInvitationsSearch(
  search: Record<string, unknown>,
): InvitationsSearch {
  return searchFlag(search.new) !== undefined ? { new: 1 } : {};
}

export interface ScenarioRowsSearch {
  import?: 1;
}

export function parseScenarioRowsSearch(
  search: Record<string, unknown>,
): ScenarioRowsSearch {
  return searchFlag(search.import) !== undefined ? { import: 1 } : {};
}

export interface VariablesSearch {
  key?: string;
  new?: 1;
  scope_id?: string;
  scope_type?: "agent" | "persona" | "scenario";
  variable?: string;
}

// One authoring subject at a time. An existing variable wins over the create
// opener, matching the other URL-addressable authoring surfaces.
export function parseVariablesSearch(
  search: Record<string, unknown>,
): VariablesSearch {
  const variable = searchUuid(search.variable);
  if (variable !== undefined) {
    return { variable };
  }
  if (searchFlag(search.new) === undefined) {
    return {};
  }
  const next: VariablesSearch = { new: 1 };
  const key = searchText(search.key);
  if (key !== undefined) {
    next.key = key;
  }
  if (
    search.scope_type === "agent" ||
    search.scope_type === "persona" ||
    search.scope_type === "scenario"
  ) {
    next.scope_type = search.scope_type;
  }
  const scopeId = searchUuid(search.scope_id);
  if (scopeId !== undefined) {
    next.scope_id = scopeId;
  }
  return next;
}

// /settings/usage period picker (usage.md): `?period=YYYY-MM` selects the
// UTC calendar month. Absent/invalid stays absent — the screen defaults to
// the current month, which stays out of the URL.
export interface UsageSettingsSearch {
  period?: string;
}

export function parseUsageSettingsSearch(
  search: Record<string, unknown>,
): UsageSettingsSearch {
  return isMonthKey(search.period) ? { period: search.period } : {};
}

// Phase 4 (teams-settings): `?new=1` opens the create-team Drawer.
export interface TeamsSearch {
  new?: 1;
}

export function parseTeamsSearch(search: Record<string, unknown>): TeamsSearch {
  return searchFlag(search.new) !== undefined ? { new: 1 } : {};
}

export type AlertsTab = "channels" | "history" | "rules" | "gate-runs";
export type AlertsCreateKind = "channel";
export type AlertsWhenFilter = "firing" | "resolved";
export type AlertsTypeFilter = "event" | "threshold";

export interface AlertsSearch {
  channel?: string;
  created_after?: string;
  created_before?: string;
  event?: string;
  new?: AlertsCreateKind;
  rule?: string;
  run?: string;
  tab?: AlertsTab;
  type?: AlertsTypeFilter;
  when?: AlertsWhenFilter;
}

// One drawer at a time (alerts.md): detail subjects and `new` are mutually
// exclusive. Detail subjects win over create: rule, channel, event, run,
// then new. Absent/invalid `tab`
// stays absent (the screen renders History by default).
export function parseAlertsSearch(
  search: Record<string, unknown>,
): AlertsSearch {
  const next: AlertsSearch = {};
  if (
    search.tab === "channels" ||
    search.tab === "history" ||
    search.tab === "rules" ||
    search.tab === "gate-runs"
  ) {
    next.tab = search.tab;
  }
  if (search.when === "firing" || search.when === "resolved") {
    next.when = search.when;
  }
  if (search.type === "event" || search.type === "threshold") {
    next.type = search.type;
  }
  const createdAfter = searchIsoTimestamp(search.created_after);
  if (createdAfter !== undefined) {
    next.created_after = createdAfter;
  }
  const createdBefore = searchIsoTimestamp(search.created_before);
  if (createdBefore !== undefined) {
    next.created_before = createdBefore;
  }
  const rule = searchText(search.rule);
  const channel = searchText(search.channel);
  const event = searchText(search.event);
  const run = searchText(search.run);
  if (rule !== undefined) {
    next.rule = rule;
  } else if (channel !== undefined) {
    next.channel = channel;
  } else if (event !== undefined) {
    next.event = event;
  } else if (run !== undefined) {
    next.run = run;
  } else if (search.new === "channel") {
    next.new = search.new;
  }
  return next;
}

export interface MarkersSearch {
  evaluator?: string;
  marker?: string;
  origin?: string;
  output?: string;
}

export const MARKER_OUTPUT_FILTERS = ["boolean", "category", "number"] as const;
export const MARKER_EVALUATOR_FILTERS = [
  "builtin",
  "human_only",
  "judge",
  "rule",
] as const;
export const MARKER_ORIGIN_FILTERS = ["builtin", "custom"] as const;

export function markerOutputFilterList(
  value: string | undefined,
): Array<(typeof MARKER_OUTPUT_FILTERS)[number]> {
  return (value?.split(",") ?? []) as Array<
    (typeof MARKER_OUTPUT_FILTERS)[number]
  >;
}

export function markerEvaluatorFilterList(
  value: string | undefined,
): Array<(typeof MARKER_EVALUATOR_FILTERS)[number]> {
  return (value?.split(",") ?? []) as Array<
    (typeof MARKER_EVALUATOR_FILTERS)[number]
  >;
}

export function markerOriginFilterList(
  value: string | undefined,
): Array<(typeof MARKER_ORIGIN_FILTERS)[number]> {
  return (value?.split(",") ?? []) as Array<
    (typeof MARKER_ORIGIN_FILTERS)[number]
  >;
}

export function parseMarkersSearch(
  search: Record<string, unknown>,
): MarkersSearch {
  const marker = searchText(search.marker);
  const output = searchCommaList(search.output, MARKER_OUTPUT_FILTERS);
  const evaluator = searchCommaList(search.evaluator, MARKER_EVALUATOR_FILTERS);
  const origin = searchCommaList(search.origin, MARKER_ORIGIN_FILTERS);
  return {
    ...(marker !== undefined ? { marker } : {}),
    ...(output !== undefined ? { output } : {}),
    ...(evaluator !== undefined ? { evaluator } : {}),
    ...(origin !== undefined ? { origin } : {}),
  };
}

export const REVIEW_EVAL_STATUS_FILTERS = [
  "ok",
  "failed",
  "not_eligible",
] as const;

export interface ReviewSearch {
  // ASSUMED(selected addresses row selection; answer opens inspection).
  selected?: string;
  answer?: string;
  marker_revision_id?: string;
  origin?: "live" | "simulated";
  batch_id?: string;
  agent_version_id?: string;
  created_after?: string;
  created_before?: string;
  sort?: "oldest" | "newest";
  eval_status?: string;
  flagged?: boolean;
  marker_id?: string;
  needs_review?: boolean | "any";
  source?: "human" | "machine";
}

function searchBoolean(value: unknown): boolean | undefined {
  if (value === true || value === "true") {
    return true;
  }
  if (value === false || value === "false") {
    return false;
  }
  return undefined;
}

export function parseReviewSearch(
  search: Record<string, unknown>,
): ReviewSearch {
  const source = search.source;
  const evalStatus = searchCommaList(
    search.eval_status,
    REVIEW_EVAL_STATUS_FILTERS,
  );
  const flagged = searchBoolean(search.flagged);
  // ASSUMED(review cleared-default token needs_review=any).
  const needsReview =
    search.needs_review === "any"
      ? "any"
      : (searchBoolean(search.needs_review) ?? true);
  const markerId = searchUuid(search.marker_id);
  const activity: ReviewSearch = {};
  for (const key of [
    "answer",
    "selected",
    "marker_revision_id",
    "batch_id",
    "agent_version_id",
  ] as const) {
    const value = searchUuid(search[key]);
    if (value !== undefined) activity[key] = value;
  }
  for (const key of ["created_after", "created_before"] as const) {
    const value = searchIsoTimestamp(search[key]);
    if (value !== undefined) activity[key] = value;
  }
  if (search.origin === "live" || search.origin === "simulated") {
    activity.origin = search.origin;
  }
  if (search.sort === "oldest" || search.sort === "newest") {
    activity.sort = search.sort;
  }
  if (markerId === undefined) delete activity.marker_revision_id;
  if (activity.origin !== "simulated") delete activity.batch_id;
  return {
    ...activity,
    ...(source === "human" || source === "machine" ? { source } : {}),
    ...(evalStatus !== undefined ? { eval_status: evalStatus } : {}),
    ...(flagged !== undefined ? { flagged } : {}),
    needs_review: needsReview,
    ...(markerId !== undefined ? { marker_id: markerId } : {}),
  };
}

// --- Phase 3 (dashboard.md, transcript-timeline.md, agent.md, transcripts.md,
// --- alignment-scorecard.md) ------------------------------------------------

// The range defaults the screens apply when `?range=` is absent (dashboard.md
// default 7d; alignment-scorecard.md default 30d). The parsed search never
// carries the default, so it stays omitted from the URL.
export const DASHBOARD_DEFAULT_RANGE: RangePreset = "7d";
export const ALIGNMENT_DEFAULT_RANGE: RangePreset = "30d";

export interface DashboardSearch {
  range?: RangePreset;
}

// `?range=` on /dashboard: 7d | 30d | 90d, default 7d applied by the screen
// (absent/invalid stays absent so the default is omitted from the URL).
export function parseDashboardSearch(
  search: Record<string, unknown>,
): DashboardSearch {
  const range = searchRangePreset(search.range);
  return range !== undefined ? { range } : {};
}

export type DashboardEditSubject = "layout";

export interface DashboardDetailSearch {
  edit?: DashboardEditSubject;
  range?: RangePreset;
  widget?: string;
}

// /dashboard/$dashboardId adds `?edit=layout` — the drag/resize layout-edit
// mode — and `?widget=<id|new>` — the widget-editor Drawer subject (§4a:
// deep-linked, one at a time). The author-only gate is container behavior
// (non-authors strip both once the authority seam resolves), not parse.
export function parseDashboardDetailSearch(
  search: Record<string, unknown>,
): DashboardDetailSearch {
  const next: DashboardDetailSearch = parseDashboardSearch(search);
  if (search.edit === "layout") {
    next.edit = "layout";
  }
  if (typeof search.widget === "string" && search.widget.length > 0) {
    next.widget = search.widget;
  }
  return next;
}

// The secondary panels of the /transcripts/$id canvas (Traces, Variables,
// Notes) open in a Drawer whose subject rides `?panel=` so refresh restores
// it (UI-LAW §4a).
// Law: docs/09a-frontend-screens.md (Transcript results rail).
export type TranscriptTimelinePanel = "notes" | "traces" | "variables";

export interface TranscriptTimelineSearch {
  feed?: string;
  label?: string;
  marker?: string;
  panel?: TranscriptTimelinePanel;
  span_id?: string;
}

export interface TraceDetailSearch {
  span_id?: string;
}

export function parseTraceDetailSearch(
  search: Record<string, unknown>,
): TraceDetailSearch {
  const spanId = searchText(search.span_id);
  return spanId === undefined ? {} : { span_id: spanId };
}

// /transcripts/$id search: `?label=<answerId|new>` is the expanded results
// row (or the Add-a-question drawer), `?marker=` accompanies `label=new`,
// `?span_id=` the trace span, `?feed=` the review continuation, `?panel=` the
// open secondary Drawer. Absent/invalid values stay absent.
export function parseTranscriptTimelineSearch(
  search: Record<string, unknown>,
): TranscriptTimelineSearch {
  const feed = searchText(search.feed);
  const label = search.label === "new" ? "new" : searchUuid(search.label);
  const marker = label === "new" ? searchUuid(search.marker) : undefined;
  const spanId = searchText(search.span_id);
  const panel = search.panel;
  const next: TranscriptTimelineSearch = {
    ...(feed === undefined ? {} : { feed }),
    ...(label === undefined ? {} : { label }),
    ...(marker === undefined ? {} : { marker }),
    ...(spanId === undefined ? {} : { span_id: spanId }),
  };
  if (panel === "notes" || panel === "traces" || panel === "variables") {
    next.panel = panel;
  }
  return next;
}

export type TranscriptStatusFilter =
  | "pending"
  | "processing"
  | "ready"
  | "failed";
export type TranscriptSourceFilter = "live" | "simulated";

export const TRANSCRIPT_STATUS_FILTERS = [
  "pending",
  "processing",
  "ready",
  "failed",
] as const satisfies readonly TranscriptStatusFilter[];

export const TRANSCRIPT_SOURCE_FILTERS = [
  "live",
  "simulated",
] as const satisfies readonly TranscriptSourceFilter[];

// Containers split the canonical comma-list params back into the typed arrays
// the API expects (`statuses` / `sources` on GET /v1/transcripts).
export function transcriptStatusFilterList(
  value: string | undefined,
): TranscriptStatusFilter[] {
  const canonical = searchCommaList(value, TRANSCRIPT_STATUS_FILTERS);
  return canonical === undefined
    ? []
    : (canonical.split(",") as TranscriptStatusFilter[]);
}

export function transcriptSourceFilterList(
  value: string | undefined,
): TranscriptSourceFilter[] {
  const canonical = searchCommaList(value, TRANSCRIPT_SOURCE_FILTERS);
  return canonical === undefined
    ? []
    : (canonical.split(",") as TranscriptSourceFilter[]);
}

export interface TranscriptsSearch {
  // Canonical comma lists of TranscriptStatusFilter / TranscriptSourceFilter
  // tokens (e.g. "ready,failed") — kept as strings so the URL stays a
  // human-readable comma list; invalid tokens are dropped.
  batch?: string;
  created_after?: string;
  created_before?: string;
  occurred_after?: string;
  occurred_before?: string;
  source?: string;
  status?: string;
  upload?: 1;
}

// Every list facet the transcript detail (and its parent crumb) carries back
// to the list. `satisfies Record<keyof Omit<TranscriptsSearch, "upload">,
// true>` makes the set compile-time exhaustive: a new facet on
// `TranscriptsSearch` breaks the build until it is either carried here or
// explicitly excluded from the `Omit` the way `upload` is (it opens a drawer,
// so returning to the list must not reopen it).
const TRANSCRIPT_LIST_RETURN_KEYS = {
  batch: true,
  created_after: true,
  created_before: true,
  occurred_after: true,
  occurred_before: true,
  source: true,
  status: true,
} as const satisfies Record<keyof Omit<TranscriptsSearch, "upload">, true>;

type TranscriptListReturnKey = keyof typeof TRANSCRIPT_LIST_RETURN_KEYS;

export function transcriptListReturnSearch(
  previous: WorkingScopeSearch & TranscriptsSearch,
): WorkingScopeSearch & Omit<TranscriptsSearch, "upload"> {
  const carried: Omit<TranscriptsSearch, "upload"> = {};
  for (const key of Object.keys(
    TRANSCRIPT_LIST_RETURN_KEYS,
  ) as TranscriptListReturnKey[]) {
    const value = previous[key];
    if (value !== undefined) {
      carried[key] = value;
    }
  }
  return { ...canonicalWorkingScopeSearch(previous), ...carried };
}

// /transcripts list facets (transcripts.md). `upload` is kept by
// `validateSearch` even when unscoped — the container ignores it without an
// `?agent=` scope (upload requires agent_id). Date-range params are ISO
// timestamps kept verbatim; invalid values are dropped.
export function parseTranscriptsSearch(
  search: Record<string, unknown>,
): TranscriptsSearch {
  const next: TranscriptsSearch = {};
  const status = searchCommaList(search.status, TRANSCRIPT_STATUS_FILTERS);
  if (status !== undefined) {
    next.status = status;
  }
  const source = searchCommaList(search.source, TRANSCRIPT_SOURCE_FILTERS);
  if (source !== undefined) {
    next.source = source;
  }
  const batch = searchUuid(search.batch);
  if (batch !== undefined) {
    next.batch = batch;
  }
  if (searchFlag(search.upload) !== undefined) {
    next.upload = 1;
  }
  const createdAfter = searchIsoTimestamp(search.created_after);
  if (createdAfter !== undefined) {
    next.created_after = createdAfter;
  }
  const createdBefore = searchIsoTimestamp(search.created_before);
  if (createdBefore !== undefined) {
    next.created_before = createdBefore;
  }
  const occurredAfter = searchIsoTimestamp(search.occurred_after);
  if (occurredAfter !== undefined) {
    next.occurred_after = occurredAfter;
  }
  const occurredBefore = searchIsoTimestamp(search.occurred_before);
  if (occurredBefore !== undefined) {
    next.occurred_before = occurredBefore;
  }
  return next;
}

export interface AgentSearch {
  connection?: string;
  new?: "connection";
  version?: string;
}

// /agent + /agents/$agentId drawer subjects (agent.md). One drawer at a time:
// `connection`, `version`, and `new` are mutually exclusive — a detail
// subject wins over a create drawer (the alerts precedence rule). Subject params are
// uuids-only; the cross-agent `agent_id` validation happens in the container
// after the detail query resolves. Personas live on the top-level /personas
// route.
export function parseAgentSearch(search: Record<string, unknown>): AgentSearch {
  const next: AgentSearch = {};
  const connection = searchUuid(search.connection);
  if (connection !== undefined) {
    next.connection = connection;
    return next;
  }
  const version = searchUuid(search.version);
  if (version !== undefined) {
    next.version = version;
    return next;
  }
  if (search.new === "connection") {
    next.new = search.new;
  }
  return next;
}

export interface PersonasSearch {
  new?: "persona";
  persona?: string;
}

// /personas drawer subjects (List archetype): `?persona=<uuid>` opens the
// edit drawer, `?new=persona` the create drawer; a detail subject wins over
// create (the alerts precedence rule).
export function parsePersonasSearch(
  search: Record<string, unknown>,
): PersonasSearch {
  const next: PersonasSearch = {};
  const persona = searchUuid(search.persona);
  if (persona !== undefined) {
    next.persona = persona;
    return next;
  }
  if (search.new === "persona") {
    next.new = "persona";
  }
  return next;
}

// The top-level /scenarios screen tabs (founder ratification 2026-07-31):
// scenario authoring plus scenario sets on one agent-scoped surface. Absent
// stays absent — the screen renders the Scenarios tab — so plain links never
// carry a redundant tab param (the transcript-timeline precedent).
export type ScenariosTab = "scenarios" | "sets";

export interface ScenariosSearch {
  new?: "scenario" | "scenario-set";
  scenario?: string;
  scenarioSet?: string;
  tab?: ScenariosTab;
}

// One drawer at a time (§4a) across BOTH tabs: a detail subject wins over a
// create drawer (the alerts precedence rule), `scenario` before `scenarioSet`.
// The surviving subject then IMPLIES its tab, so a hand-edited URL can never
// strand a drawer param behind the wrong tab: `scenarioSet` / `new=scenario-set`
// force the Sets tab, `scenario` / `new=scenario` the Scenarios tab. With no
// subject, `tab` stands on its own — it is the screen facet, not a subject.
// "scenarios" is the screen default, so an IMPLIED scenarios tab stays out of
// the URL (an explicit `?tab=scenarios` is still kept verbatim).
export function parseScenariosSearch(
  search: Record<string, unknown>,
): ScenariosSearch {
  const next: ScenariosSearch = {};
  const validTab =
    search.tab === "scenarios" || search.tab === "sets"
      ? search.tab
      : undefined;
  const scenario = searchUuid(search.scenario);
  const scenarioSet = searchUuid(search.scenarioSet);
  let impliedTab: ScenariosTab | undefined;
  if (scenario !== undefined) {
    next.scenario = scenario;
    impliedTab = "scenarios";
  } else if (scenarioSet !== undefined) {
    next.scenarioSet = scenarioSet;
    impliedTab = "sets";
  } else if (search.new === "scenario" || search.new === "scenario-set") {
    next.new = search.new;
    impliedTab = search.new === "scenario-set" ? "sets" : "scenarios";
  }
  const tab = impliedTab ?? validTab;
  if (tab !== undefined && (tab === "sets" || validTab === "scenarios")) {
    next.tab = tab;
  }
  return next;
}

// The legacy agent-nested authoring routes' schemas. Both routes are now
// redirects onto `/scenarios` (founder ratification 2026-07-31); validating
// their search here keeps an old deep link's drawer subject intact across the
// redirect and still drops a malformed id.
export interface ScenarioSetsSearch {
  new?: "scenario-set";
  scenarioSet?: string;
}

export function parseScenarioSetsSearch(
  search: Record<string, unknown>,
): ScenarioSetsSearch {
  const scenarioSet = searchUuid(search.scenarioSet);
  if (scenarioSet !== undefined) {
    return { scenarioSet };
  }
  return search.new === "scenario-set" ? { new: "scenario-set" } : {};
}

// The legacy `/templates` route's schema. Templates folded into
// `/simulations?tab=templates` (founder ratification 2026-07-31) and the route
// is now a redirect. Its old drawer subjects are no longer search state
// anywhere — authoring is a Route (UI-LAW §4a) — so these params survive only
// here, to re-target a legacy deep link at the right authoring route:
// `?new=template` → /simulations/templates/new, `?template=<uuid>` →
// /simulations/templates/<uuid>, neither → the Templates tab.
export interface TemplatesSearch {
  new?: "template";
  template?: string;
}

export function parseTemplatesSearch(
  search: Record<string, unknown>,
): TemplatesSearch {
  const template = searchUuid(search.template);
  if (template !== undefined) {
    return { template };
  }
  return search.new === "template" ? { new: "template" } : {};
}

export type AlignmentVerdictClass = "fail" | "pass" | "warn";

// The matrix-cell drill param: `<human>-<machine>`, off-diagonal only (a
// diagonal cell is agreement — there is nothing to drill into).
export type AlignmentCellParam =
  `${AlignmentVerdictClass}-${AlignmentVerdictClass}`;

function isAlignmentVerdictClass(
  value: string,
): value is AlignmentVerdictClass {
  return value === "fail" || value === "pass" || value === "warn";
}

function searchAlignmentCell(value: unknown): AlignmentCellParam | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const parts = value.split("-");
  const human = parts[0];
  const machine = parts[1];
  if (
    parts.length !== 2 ||
    human === undefined ||
    machine === undefined ||
    !isAlignmentVerdictClass(human) ||
    !isAlignmentVerdictClass(machine) ||
    human === machine
  ) {
    return undefined;
  }
  return `${human}-${machine}`;
}

export interface AlignmentLaunchSearch {
  transcript_ids: string[];
  labels_as_of: string;
  launch_key: string;
  candidate_revision_id: string;
  baseline_revision_id: string;
}

export interface AlignmentSearch {
  reviewer?: string;
  flagged?: true;
  // ASSUMED(Alignment drawer state uses validated search fields, following
  // the existing marker/cell convention; return state is data, never a URL.)
  baseline?: string;
  inspect?: string;
  evaluate?: true;
  launch?: AlignmentLaunchSearch;
  batch?: string;
  cell?: AlignmentCellParam;
  marker?: string;
  range?: RangePreset;
  human_side?: "latest" | "resolved" | "majority";
  source?: TranscriptSourceFilter;
  version?: string;
}

// Review Alignment filters + drills (alignment-scorecard.md). The screen
// applies the 30d default (absent/invalid stays absent, keeping the default out
// of the URL). The named batch picker carries a UUID. Filter changes reset
// drills in the container; invalid URL subjects are dropped here.
export function parseAlignmentSearch(
  search: Record<string, unknown>,
): AlignmentSearch {
  const next: AlignmentSearch = {};
  const range = searchRangePreset(search.range);
  if (range !== undefined) {
    next.range = range;
  }
  if (
    search.human_side === "latest" ||
    search.human_side === "resolved" ||
    search.human_side === "majority"
  ) {
    next.human_side = search.human_side;
  }
  const version = searchUuid(search.version);
  if (version !== undefined) {
    next.version = version;
  }
  if (search.source === "live" || search.source === "simulated") {
    next.source = search.source;
  }
  const batch = searchUuid(search.batch);
  if (batch !== undefined) next.batch = batch;
  const cell = searchAlignmentCell(search.cell);
  if (cell !== undefined) {
    next.cell = cell;
  }
  const marker = searchUuid(search.marker);
  if (marker !== undefined) {
    next.marker = marker;
  }
  const reviewer = searchUuid(search.reviewer);
  if (reviewer) next.reviewer = reviewer;
  if (search.flagged === true) next.flagged = true;
  const baseline = searchUuid(search.baseline);
  if (baseline) next.baseline = baseline;
  const inspect = searchUuid(search.inspect);
  if (inspect) next.inspect = inspect;
  if (search.evaluate === true) next.evaluate = true;
  const launch = search.launch;
  if (typeof launch === "object" && launch !== null) {
    const value = launch as Record<string, unknown>;
    const ids = value.transcript_ids;
    const labels = searchIsoTimestamp(value.labels_as_of);
    const key = searchUuid(value.launch_key);
    const candidate = searchUuid(value.candidate_revision_id);
    const original = searchUuid(value.baseline_revision_id);
    if (
      Array.isArray(ids) &&
      ids.length > 0 &&
      ids.every((id) => searchUuid(id) !== undefined) &&
      labels &&
      /(?:Z|[+-]00:00)$/.test(labels) &&
      key &&
      candidate &&
      original
    ) {
      next.launch = {
        transcript_ids: ids as string[],
        labels_as_of: labels,
        launch_key: key,
        candidate_revision_id: candidate,
        baseline_revision_id: original,
      };
    }
  }
  return next;
}

// Review owns the tab; each existing body keeps its own search contract.
export type ReviewTab = "activity" | "alignment";
export type ReviewRouteSearch =
  | (ReviewSearch & { tab?: "activity" })
  | (AlignmentSearch & { tab: "alignment" });

export function parseReviewRouteSearch(
  search: Record<string, unknown>,
): ReviewRouteSearch {
  if (search.tab === "alignment") {
    return { ...parseAlignmentSearch(search), tab: "alignment" };
  }
  return {
    ...parseReviewSearch(search),
    ...(search.tab === "activity" ? { tab: "activity" as const } : {}),
  };
}

// Both tabs start fresh on entry; only the root-owned scope survives.
export function reviewTabSearch(tab: ReviewTab) {
  return (previous: WorkingScopeSearch) => ({
    ...canonicalWorkingScopeSearch(previous),
    tab,
  });
}

export type MarkerEditMode = "automate" | "revise";

export interface MarkerEditSearch {
  mode: MarkerEditMode;
  alignment_return?: AlignmentSearch;
}

// Missing/invalid coerces to "revise" (markers.md), so the parsed search
// always carries a mode.
export function parseMarkerEditSearch(
  search: Record<string, unknown>,
): MarkerEditSearch {
  const state = search.alignment_return;
  return {
    mode: search.mode === "automate" ? "automate" : "revise",
    ...(typeof state === "object" && state !== null
      ? {
          alignment_return: parseAlignmentSearch(
            state as Record<string, unknown>,
          ),
        }
      : {}),
  };
}

// The /simulations screen tabs (founder ratification 2026-07-31): Templates
// folded into the launch hub as a second tab. Absent stays absent — the
// screen renders Runs — so a plain /simulations link never carries a
// redundant tab param (the transcript-timeline precedent).
export type SimulationsTab = "runs" | "templates";

export interface SimulationsSearch {
  // The batch a just-completed launch highlights in the queue (simulations.md
  // post-launch law): /simulations?launched=<batchId>.
  launched?: string;
  tab?: SimulationsTab;
}

// `launched` is the Runs tab's highlight. The screen carries no drawer
// subject: template authoring is a Route (UI-LAW §4a), so the only tab state
// left is `?tab`. "runs" is the screen default and stays out of the URL
// unless asked for explicitly.
export function parseSimulationsSearch(
  search: Record<string, unknown>,
): SimulationsSearch {
  const next: SimulationsSearch = {};
  const launched = searchUuid(search.launched);
  if (launched !== undefined) {
    next.launched = launched;
  }
  if (search.tab === "runs" || search.tab === "templates") {
    next.tab = search.tab;
  }
  return next;
}

export interface SimulationCreateSearch {
  // Duplicate-into-form: prefill the launch form from a past batch's spec
  // (simulations.md): /simulations/new?from=<batchId>.
  from?: string;
  // Run a saved template through the same preview/Create form. Mutually
  // exclusive with `from`; a template subject wins during normalization.
  template?: string;
}

export function parseSimulationCreateSearch(
  search: Record<string, unknown>,
): SimulationCreateSearch {
  const next: SimulationCreateSearch = {};
  const template = searchUuid(search.template);
  if (template !== undefined) {
    next.template = template;
    return next;
  }
  const from = searchUuid(search.from);
  if (from !== undefined) {
    next.from = from;
  }
  return next;
}

export interface CheckoutSearch {
  plan?: string;
}

export function parseCheckoutSearch(
  search: Record<string, unknown>,
): CheckoutSearch {
  return typeof search.plan === "string" && search.plan.trim()
    ? { plan: search.plan.trim() }
    : {};
}

export function checkoutPlanKey(
  search: CheckoutSearch,
  availablePlanKeys: readonly string[],
): string {
  return search.plan && availablePlanKeys.includes(search.plan)
    ? search.plan
    : (availablePlanKeys[0] ?? "");
}
